import { Fragment, type HTMLAttributes, type SyntheticEvent } from 'react';
import {
  Autocomplete,
  Box,
  TextField,
  Typography,
  type AutocompleteRenderInputParams,
  type SxProps,
  type Theme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

interface CreateOption {
  inputValue: string;
  isCreate: true;
}

type OptionType = string | CreateOption;

const isCreateOption = (option: OptionType): option is CreateOption =>
  typeof option !== 'string' && 'isCreate' in option;

interface FacetAutocompleteBaseProps {
  options: string[];
  placeholder?: string;
  disabled?: boolean;
  matchLabel?: string;
  sx?: SxProps<Theme>;
}

interface FacetAutocompleteSingleProps extends FacetAutocompleteBaseProps {
  multiple?: false;
  value: string | null;
  onChange: (value: string) => void;
}

interface FacetAutocompleteMultipleProps extends FacetAutocompleteBaseProps {
  multiple: true;
  value: string[];
  onChange: (value: string[]) => void;
}

type FacetAutocompleteProps = FacetAutocompleteSingleProps | FacetAutocompleteMultipleProps;

const filterOptions = (opts: OptionType[], state: { inputValue: string }): OptionType[] => {
  const inputValue = state.inputValue.trim();
  if (!inputValue) return opts;

  const filtered: OptionType[] = (opts as string[]).filter(
    opt => typeof opt === 'string' && opt.toLowerCase().includes(inputValue.toLowerCase()),
  );

  const exactMatch = (opts as string[]).some(
    opt => typeof opt === 'string' && opt.toLowerCase() === inputValue.toLowerCase(),
  );
  if (!exactMatch) {
    filtered.push({ inputValue, isCreate: true });
  }

  return filtered;
};

const getOptionLabel = (option: OptionType): string => {
  if (isCreateOption(option)) return option.inputValue;
  return typeof option === 'string' ? option : '';
};

const FacetAutocomplete = (props: FacetAutocompleteProps) => {
  const { options, placeholder, disabled, matchLabel = 'Tag Matches', sx, multiple } = props;

  const renderOption = (
    liProps: HTMLAttributes<HTMLLIElement>,
    option: OptionType,
    state: { index: number },
  ) => {
    const { key, ...rest } = liProps as HTMLAttributes<HTMLLIElement> & { key: string };

    if (isCreateOption(option)) {
      const hasNoMatches = state.index === 0;
      return (
        <Fragment key="__create__">
          {hasNoMatches && (
            <Typography
              variant="body2"
              sx={{ px: 2, py: 0.5, color: 'error.main', fontWeight: 600 }}
            >
              No matches found
            </Typography>
          )}
          <Box
            component="li"
            {...rest}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
          >
            <AddIcon fontSize="small" />
            Create &ldquo;{option.inputValue}&rdquo;
          </Box>
        </Fragment>
      );
    }

    return (
      <Fragment key={key}>
        {state.index === 0 && (
          <Typography variant="body2" sx={{ px: 2, py: 0.5, fontWeight: 700 }}>
            {matchLabel}
          </Typography>
        )}
        <li {...rest}>{option}</li>
      </Fragment>
    );
  };

  const renderInput = (params: AutocompleteRenderInputParams) => (
    <TextField
      {...params}
      placeholder={placeholder}
      InputProps={{ ...params.InputProps, sx: { bgcolor: 'background.paper' } }}
    />
  );

  if (multiple) {
    const { onChange, value } = props as FacetAutocompleteMultipleProps;
    return (
      <Autocomplete<OptionType, true, false, true>
        size="small"
        freeSolo
        autoSelect
        multiple
        sx={sx}
        disabled={disabled}
        options={options as OptionType[]}
        value={value as OptionType[]}
        getOptionLabel={getOptionLabel}
        filterOptions={filterOptions}
        renderOption={renderOption}
        onChange={(_: SyntheticEvent, newValue: OptionType[]) => {
          onChange(newValue.map(v => (isCreateOption(v) ? v.inputValue : (v as string))));
        }}
        renderInput={renderInput}
      />
    );
  }

  const { onChange, value } = props as FacetAutocompleteSingleProps;
  return (
    <Autocomplete<OptionType, false, false, true>
      size="small"
      freeSolo
      autoSelect
      sx={sx}
      disabled={disabled}
      options={options as OptionType[]}
      value={(value ?? null) as OptionType | null}
      getOptionLabel={getOptionLabel}
      filterOptions={filterOptions}
      renderOption={renderOption}
      onChange={(_: SyntheticEvent, newValue: OptionType | null) => {
        if (newValue === null) {
          onChange('');
        } else if (isCreateOption(newValue)) {
          onChange(newValue.inputValue);
        } else {
          onChange(newValue as string);
        }
      }}
      renderInput={renderInput}
    />
  );
};

export default FacetAutocomplete;
